import { useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {
  ArrowLeft,
  Video,
  Shield,
  ExternalLink,
  FileText,
  Clock,
  Sparkles,
  Eye,
  EyeOff,
  Save,
  Mic,
  Lock,
  Trash2,
  CheckCircle,
  User,
} from 'lucide-react'
import { interviewerApi } from '../../lib/api/interviewer'
import { evaluationsApi } from '../../lib/api/evaluations'

interface SessionDetail {
  id: number
  title: string
  client_name: string
  client_email?: string
  scheduled_at: string
  duration_minutes: number
  status: string
  zoom_url?: string
  target_role?: string
}

interface Question {
  id?: number
  question: string
  category?: string
}

interface Note {
  id: number
  content: string
  is_private: boolean
  created_at: string
}

export default function InterviewSession() {
  const { id, bookingId } = useParams<{ id?: string; bookingId?: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const navState = location.state as {
    client_name?: string | null
    client_email?: string | null
    zoom_url?: string | null
    zoom_password?: string | null
    scheduled_at?: string
    duration_minutes?: number
    status?: string
  } | null

  const [session, setSession] = useState<SessionDetail | null>(null)
  const [zoomPassword, setZoomPassword] = useState<string | null>(null)
  const [connected, setConnected] = useState(false)
  const [questions, setQuestions] = useState<Question[]>([])
  const [notes, setNotes] = useState<Note[]>([])
  const [newNote, setNewNote] = useState('')
  const [isNotePrivate, setIsNotePrivate] = useState(true)
  const [loading, setLoading] = useState(true)
  const [generatingQuestions, setGeneratingQuestions] = useState(false)
  const [savingNote, setSavingNote] = useState(false)
  const [error, setError] = useState('')
  const [retentionDays] = useState(30)

  useEffect(() => {
    if (!id && !bookingId) return

    const fetchData = async () => {
      try {
        if (bookingId) {
          const numId = parseInt(bookingId)
          if (navState?.scheduled_at) {
            setSession({
              id: numId,
              title: 'Interview Session',
              client_name: navState.client_name || 'Client',
              client_email: navState.client_email ?? undefined,
              scheduled_at: navState.scheduled_at,
              duration_minutes: navState.duration_minutes ?? 60,
              status: navState.status ?? 'confirmed',
              zoom_url: navState.zoom_url ?? undefined,
            })
            setZoomPassword(navState.zoom_password ?? null)
          } else {
            const data = await interviewerApi.getBooking(numId)
            setSession({
              id: numId,
              title: 'Interview Session',
              client_name: data.client_name || data.invitee_name || 'Client',
              client_email: data.client_email || data.invitee_email,
              scheduled_at: data.scheduled_start_utc ?? data.scheduled_at,
              duration_minutes: data.duration_minutes ?? 60,
              status: data.status,
              zoom_url: data.zoom_join_url ?? data.zoom_url,
            })
            setZoomPassword(data.zoom_password ?? null)
          }
          setLoading(false)
          return
        }

        const sessionData = await interviewerApi.getSession(parseInt(id!))
        setSession(sessionData)

        const [questionsRes, notesRes] = await Promise.all([
          evaluationsApi.getQuestionSet(parseInt(id!)).catch(() => null),
          evaluationsApi.getNotes(parseInt(id!)).catch(() => []),
        ])
        if (questionsRes?.questions) setQuestions(questionsRes.questions)
        setNotes(notesRes || [])
      } catch (err) {
        console.error('Failed to fetch session:', err)
        setError('Failed to load session data.')
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [id, bookingId]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerateQuestions = async () => {
    if (!id) return
    setGeneratingQuestions(true)
    try {
      const result = await evaluationsApi.generateQuestions(parseInt(id))
      setQuestions(result.questions || result || [])
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to generate questions.')
    } finally {
      setGeneratingQuestions(false)
    }
  }

  const handleSaveNote = async () => {
    if (!newNote.trim() || !id) return
    setSavingNote(true)
    try {
      const created = await evaluationsApi.createNote({
        session_id: parseInt(id),
        content: newNote.trim(),
        is_private: isNotePrivate,
      })
      setNotes([...notes, created])
      setNewNote('')
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save note.')
    } finally {
      setSavingNote(false)
    }
  }

  const handleLaunchZoom = () => {
    if (!session?.zoom_url) return
    const url = session.zoom_url
    const idMatch = url.match(/\/j\/(\d+)/)
    const pwdMatch = url.match(/[?&]pwd=([^&]+)/)
    if (idMatch) {
      const confno = idMatch[1]
      const pwd = pwdMatch ? pwdMatch[1] : ''
      window.location.href = `zoommtg://zoom.us/join?action=join&confno=${confno}${pwd ? `&pwd=${pwd}` : ''}`
      setTimeout(() => window.open(url, '_blank', 'noopener,noreferrer'), 2000)
    } else {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
    setConnected(true)
  }

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
    })

  const fmtTime = (iso: string) =>
    new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

  if (loading) return <div className="loading-screen">Loading session...</div>

  if (error || !session) {
    return (
      <div className="empty-state">
        <FileText size={48} />
        <h3>{error || 'Session not found'}</h3>
        <button
          className="btn btn-primary"
          onClick={() => navigate(bookingId ? '/interviewer' : '/interviewer/sessions')}
        >
          {bookingId ? 'Back to Dashboard' : 'Back to Sessions'}
        </button>
      </div>
    )
  }

  const isCompleted = session.status === 'completed'
  const statusColor = isCompleted ? '#16a34a' : '#2563eb'
  const statusBg = isCompleted ? 'rgba(22,163,74,0.1)' : 'rgba(37,99,235,0.1)'
  const statusLabel = isCompleted ? 'Completed' : session.status.charAt(0).toUpperCase() + session.status.slice(1)

  return (
    <div className="is-page">

      {/* ── Header ── */}
      <div className="cs-header">
        <button
          className="cs-back-btn"
          type="button"
          onClick={() => navigate(bookingId ? '/interviewer' : '/interviewer/sessions')}
        >
          <ArrowLeft size={16} />
          {bookingId ? 'Back' : 'Back'}
        </button>
        <h1 className="cs-heading">Interview Session</h1>
        <span className="cs-status-pill" style={{ background: statusBg, color: statusColor }}>
          {isCompleted ? <CheckCircle size={12} /> : <Clock size={12} />}
          {statusLabel}
        </span>
      </div>

      {session.scheduled_at && (
        <div className="cs-date-bar">
          <Clock size={14} />
          <span>{fmtDate(session.scheduled_at)}</span>
          <span className="cs-date-sep">·</span>
          <span>{fmtTime(session.scheduled_at)}</span>
          <span className="cs-date-sep">·</span>
          <span>{session.duration_minutes} min</span>
        </div>
      )}

      {/* ── Main layout ── */}
      <div className="cs-layout">

        {/* ── Left / Main column ── */}
        <div className="cs-main">

          {/* Video Conference Area */}
          <div className="cs-video-area">
            <div className="cs-video-label">
              <Video size={14} />
              Video Conference Area
            </div>
            <div className="cs-video-inner">
              <div className="cs-zoom-icon-wrap">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <rect width="48" height="48" rx="12" fill="#2D8CFF" />
                  <path d="M8 16.5C8 14.567 9.567 13 11.5 13H27C28.933 13 30.5 14.567 30.5 16.5V28.5C30.5 30.433 28.933 32 27 32H11.5C9.567 32 8 30.433 8 28.5V16.5Z" fill="white"/>
                  <path d="M32 20.5L40 15V33L32 27.5V20.5Z" fill="white"/>
                </svg>
              </div>
              <h2 className="cs-video-title">Launch Zoom Meeting</h2>
              <p className="cs-video-desc">
                Click "Connect to Zoom" to open the meeting.<br />
                For best results, use the <strong style={{ color: '#93c5fd' }}>Zoom desktop app</strong>.
              </p>
              <div className="cs-identity-notice">
                <Shield size={15} style={{ flexShrink: 0 }} />
                <span>
                  <strong>Identity Protected:</strong> Your AI avatar is displayed to the client.
                  No personal photos or identifiable information is shared.
                </span>
              </div>
            </div>
          </div>

          {/* Recording & Transcript */}
          <div className="cs-card">
            <div className="cs-card-header">
              <Video size={16} style={{ color: 'var(--primary-navy)' }} />
              <h3>Recording &amp; Transcript</h3>
            </div>
            <div className="cs-detail-grid">
              <div className="cs-detail-row">
                <span className="cs-detail-label">Zoom Meeting:</span>
                <span className="cs-detail-val">
                  <ExternalLink size={13} style={{ color: '#2563eb' }} />
                  Opens via Zoom app (not embedded)
                </span>
              </div>
              <div className="cs-detail-row">
                <span className="cs-detail-label">Transcript Status:</span>
                <span className="cs-detail-val">
                  <Mic size={13} style={{ color: connected ? '#16a34a' : '#9ca3af' }} />
                  {connected ? 'Connected' : 'Awaiting connection'}
                </span>
              </div>
              <div className="cs-detail-row">
                <span className="cs-detail-label">Recording Storage:</span>
                <span className="cs-detail-val">
                  <Lock size={13} style={{ color: '#7c3aed' }} />
                  Secure encrypted storage
                </span>
              </div>
              <div className="cs-detail-row">
                <span className="cs-detail-label">Auto-Delete:</span>
                <span className="cs-detail-val">
                  <Trash2 size={13} />
                  {retentionDays} days until auto-delete
                </span>
              </div>
            </div>
          </div>

          {/* Question Set — only for regular sessions */}
          {!bookingId && (
            <div className="cs-card">
              <div className="cs-card-header" style={{ justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <FileText size={16} style={{ color: 'var(--primary-navy)' }} />
                  <h3>Question Set</h3>
                </div>
                <button
                  className="btn btn-outline"
                  style={{ padding: '5px 12px', fontSize: 12 }}
                  onClick={handleGenerateQuestions}
                  disabled={generatingQuestions}
                >
                  <Sparkles size={13} />
                  {generatingQuestions ? 'Generating...' : 'Generate AI Questions'}
                </button>
              </div>
              {questions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: 13 }}>
                  No questions yet. Click "Generate AI Questions" to create tailored questions.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {questions.map((q, idx) => (
                    <div key={q.id || idx} className="cs-detail-row" style={{ gap: 12 }}>
                      <span style={{
                        width: 22, height: 22, borderRadius: '50%',
                        background: 'var(--primary-navy)', color: 'white',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700, flexShrink: 0,
                      }}>{idx + 1}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13 }}>{q.question}</div>
                        {q.category && (
                          <span className="badge badge-primary" style={{ fontSize: 10, marginTop: 4 }}>{q.category}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Session Notes — only for regular sessions */}
          {!bookingId && (
            <div className="cs-card">
              <div className="cs-card-header">
                <FileText size={16} style={{ color: 'var(--primary-navy)' }} />
                <h3>Session Notes</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 260, overflowY: 'auto', marginBottom: 14 }}>
                {notes.length === 0 ? (
                  <p style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 0', textAlign: 'center' }}>
                    No notes yet. Add notes below.
                  </p>
                ) : (
                  notes.map((note) => (
                    <div key={note.id} style={{
                      padding: 12, background: '#f8f9fb', borderRadius: 8,
                      borderLeft: note.is_private ? '3px solid #d97706' : '3px solid #16a34a',
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: note.is_private ? '#d97706' : '#16a34a', display: 'flex', alignItems: 'center', gap: 4 }}>
                          {note.is_private ? <EyeOff size={10} /> : <Eye size={10} />}
                          {note.is_private ? 'Private' : 'Shared'}
                        </span>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {new Date(note.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        </span>
                      </div>
                      <p style={{ fontSize: 13 }}>{note.content}</p>
                    </div>
                  ))
                )}
              </div>
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14 }}>
                <textarea
                  className="form-input"
                  placeholder="Add a note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  rows={3}
                  style={{ marginBottom: 8, resize: 'vertical', fontFamily: 'inherit' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={isNotePrivate} onChange={(e) => setIsNotePrivate(e.target.checked)} />
                    {isNotePrivate ? <EyeOff size={13} /> : <Eye size={13} />}
                    {isNotePrivate ? 'Private note' : 'Shared with client'}
                  </label>
                  <button
                    className="btn btn-primary"
                    style={{ padding: '7px 14px', fontSize: 13 }}
                    onClick={handleSaveNote}
                    disabled={savingNote || !newNote.trim()}
                  >
                    <Save size={13} />
                    {savingNote ? 'Saving...' : 'Save Note'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Right sidebar ── */}
        <div className="cs-sidebar">

          {/* Connect to Zoom */}
          {!isCompleted && (
            <div className="cs-connect-card">
              {zoomPassword && (
                <div className="cs-pwd-row">
                  <Lock size={13} />
                  <span>Meeting Password:</span>
                  <strong>{zoomPassword}</strong>
                </div>
              )}
              <button
                className="cs-connect-btn"
                onClick={handleLaunchZoom}
                disabled={!session.zoom_url}
              >
                <Video size={18} />
                {connected ? 'Rejoin Zoom Meeting' : 'Connect to Zoom'}
              </button>
              {!session.zoom_url && (
                <p className="cs-no-link">Zoom link will be available closer to your session time.</p>
              )}
              {connected && (
                <p className="cs-connected-msg">
                  <CheckCircle size={13} style={{ color: '#16a34a' }} />
                  Zoom opened — join from your Zoom app
                </p>
              )}
            </div>
          )}

          {/* Client Info */}
          <div className="cs-card">
            <div className="cs-card-header">
              <User size={16} style={{ color: 'var(--primary-navy)' }} />
              <h3>Client Info</h3>
            </div>
            <div className="cs-session-info-rows">
              <div className="cs-session-info-row">
                <span className="cs-session-info-label">Client</span>
                <span className="cs-session-info-val">
                  {session.client_name || <span style={{ color: '#9ca3af' }}>—</span>}
                </span>
              </div>
              {session.client_email && (
                <div className="cs-session-info-row">
                  <span className="cs-session-info-label">Email</span>
                  <span className="cs-session-info-val" style={{ fontSize: 12, wordBreak: 'break-all' }}>
                    {session.client_email}
                  </span>
                </div>
              )}
              <div className="cs-session-info-row">
                <span className="cs-session-info-label">Date</span>
                <span className="cs-session-info-val">
                  {fmtDate(session.scheduled_at)}
                </span>
              </div>
              <div className="cs-session-info-row">
                <span className="cs-session-info-label">Time</span>
                <span className="cs-session-info-val">{fmtTime(session.scheduled_at)}</span>
              </div>
              <div className="cs-session-info-row">
                <span className="cs-session-info-label">Duration</span>
                <span className="cs-session-info-val">{session.duration_minutes} min</span>
              </div>
              {session.target_role && (
                <div className="cs-session-info-row">
                  <span className="cs-session-info-label">Role</span>
                  <span className="cs-session-info-val">{session.target_role}</span>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      <style>{`
        .is-page { max-width: 1200px; }

        .cs-header {
          display: flex; align-items: center; gap: 14px; margin-bottom: 10px;
        }
        .cs-back-btn {
          display: flex; align-items: center; gap: 6px; padding: 7px 14px;
          background: none; border: 1px solid var(--border); border-radius: 8px;
          font-size: 13px; font-weight: 500; color: var(--text-dark); cursor: pointer; transition: all 0.15s;
        }
        .cs-back-btn:hover { background: #f3f4f6; border-color: #9ca3af; }
        .cs-heading { font-size: 22px; font-weight: 700; flex: 1; color: var(--text-dark); }
        .cs-status-pill {
          display: inline-flex; align-items: center; gap: 5px; padding: 5px 12px;
          border-radius: 20px; font-size: 12px; font-weight: 600;
        }
        .cs-date-bar {
          display: flex; align-items: center; gap: 10px; padding: 10px 16px;
          background: #f8f9fb; border-radius: 8px; font-size: 13.5px;
          color: var(--text-muted); margin-bottom: 22px; border: 1px solid var(--border);
        }
        .cs-date-sep { color: #d1d5db; }

        .cs-layout {
          display: grid; grid-template-columns: 1fr 300px; gap: 22px; align-items: start;
        }
        .cs-main { display: flex; flex-direction: column; gap: 18px; }
        .cs-sidebar { display: flex; flex-direction: column; gap: 16px; }

        .cs-video-area { border-radius: 14px; overflow: hidden; border: 1px solid #2d3a4a; }
        .cs-video-label {
          display: flex; align-items: center; gap: 7px; padding: 10px 18px;
          background: #1e2a38; color: #94a3b8; font-size: 12px; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.5px;
        }
        .cs-video-inner {
          background: linear-gradient(160deg, #0f1923 0%, #1a2535 100%);
          min-height: 300px; display: flex; flex-direction: column;
          align-items: center; justify-content: center; gap: 16px;
          padding: 40px 32px; text-align: center;
        }
        .cs-zoom-icon-wrap { margin-bottom: 4px; filter: drop-shadow(0 4px 16px rgba(45,140,255,0.4)); }
        .cs-video-title { font-size: 22px; font-weight: 700; color: white; margin: 0; }
        .cs-video-desc { font-size: 13.5px; color: #94a3b8; line-height: 1.6; max-width: 380px; margin: 0; }
        .cs-identity-notice {
          display: flex; align-items: flex-start; gap: 10px;
          padding: 12px 18px; background: rgba(45,55,72,0.5);
          border: 1px solid rgba(255,255,255,0.08); border-radius: 10px;
          color: #cbd5e1; font-size: 13px; line-height: 1.5;
          max-width: 420px; text-align: left; margin-top: 4px;
        }
        .cs-identity-notice strong { color: #e2e8f0; }

        .cs-card {
          background: white; border-radius: 12px;
          border: 1px solid var(--border); padding: 18px 20px;
        }
        .cs-card-header {
          display: flex; align-items: center; gap: 8px; margin-bottom: 14px;
        }
        .cs-card-header h3 { font-size: 15px; font-weight: 600; color: var(--text-dark); margin: 0; }

        .cs-detail-grid { display: flex; flex-direction: column; gap: 10px; }
        .cs-detail-row {
          display: flex; align-items: center; gap: 12px;
          padding: 9px 12px; background: #f8f9fb; border-radius: 8px;
        }
        .cs-detail-label { font-size: 13px; color: var(--text-muted); min-width: 160px; font-weight: 500; }
        .cs-detail-val {
          display: flex; align-items: center; gap: 6px;
          font-size: 13px; color: var(--text-dark); font-weight: 500;
        }

        .cs-connect-card {
          background: white; border-radius: 12px; border: 1px solid var(--border);
          padding: 18px 20px; display: flex; flex-direction: column; gap: 12px;
        }
        .cs-pwd-row {
          display: flex; align-items: center; gap: 7px; font-size: 13px;
          color: var(--text-muted); padding: 8px 12px; background: #f8f9fb; border-radius: 8px;
        }
        .cs-pwd-row strong { color: var(--text-dark); font-weight: 700; letter-spacing: 1px; }
        .cs-connect-btn {
          display: flex; align-items: center; justify-content: center; gap: 10px;
          width: 100%; padding: 14px; background: #2D8CFF; color: white;
          border: none; border-radius: 10px; font-size: 15px; font-weight: 700;
          cursor: pointer; transition: all 0.2s; letter-spacing: 0.2px;
        }
        .cs-connect-btn:hover:not(:disabled) {
          background: #1a7aee; transform: translateY(-1px);
          box-shadow: 0 4px 16px rgba(45,140,255,0.35);
        }
        .cs-connect-btn:disabled { background: #e5e7eb; color: #9ca3af; cursor: not-allowed; }
        .cs-no-link { font-size: 12px; color: var(--text-muted); text-align: center; margin: 0; line-height: 1.5; }
        .cs-connected-msg {
          display: flex; align-items: center; justify-content: center; gap: 6px;
          font-size: 12.5px; color: #16a34a; font-weight: 500; margin: 0;
        }

        .cs-session-info-rows { display: flex; flex-direction: column; gap: 8px; }
        .cs-session-info-row {
          display: flex; justify-content: space-between; align-items: center;
          padding: 7px 10px; background: #f8f9fb; border-radius: 7px; gap: 8px;
        }
        .cs-session-info-label { font-size: 12px; color: var(--text-muted); font-weight: 500; white-space: nowrap; }
        .cs-session-info-val { font-size: 13px; font-weight: 600; color: var(--text-dark); text-align: right; }

        @media (max-width: 900px) {
          .cs-layout { grid-template-columns: 1fr; }
          .cs-sidebar { order: -1; }
          .cs-connect-card { order: -2; }
        }
      `}</style>
    </div>
  )
}
